import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { CardRadio } from '@/components/ui/CardRadio'
import { SearchInput } from '@/components/ui/search-input'
import { filterOptions, type SearchableSelectOption } from '@/components/ui/searchable-select'
import type { FieldInputProps } from './fieldInputProps'

const MAX_MATCHES = 8

/** A trade: a card, a pick from the search list behind them, or the customer's own words. */
export function TradeField({ field, value, onChange, id, invalid, disabled }: FieldInputProps<string>) {
  const [query, setQuery] = useState('')
  const searchable: SearchableSelectOption[] = field.searchOptions.map((option) => ({
    value: option.value,
    label: option.label,
    description: option.help || undefined,
  }))
  const text = query.trim()
  const matches = text ? filterOptions(searchable, text).slice(0, MAX_MATCHES) : []
  const exactMatch = matches.some((option) => option.label.toLowerCase() === text.toLowerCase())
  const isCard = field.options.some((option) => option.value === value)
  const pickedLabel = value && !isCard ? (searchable.find((o) => o.value === value)?.label ?? value) : null

  function pick(next: string) {
    onChange(next)
    setQuery('')
  }

  return (
    <div className="flex flex-col gap-3">
      <div id={id} role="radiogroup" className="grid gap-2 sm:grid-cols-2">
        {field.options.map((option) => (
          <CardRadio
            key={option.value}
            isSelected={value === option.value}
            onSelect={() => pick(option.value)}
            disabled={disabled}
            className={invalid ? 'border-destructive' : undefined}
          >
            <span className="block">{option.label}</span>
            {option.help && (
              <span className="text-muted-foreground block text-xs font-normal">{option.help}</span>
            )}
          </CardRadio>
        ))}
        {pickedLabel && (
          <CardRadio isSelected onSelect={() => undefined} disabled={disabled}>
            {pickedLabel}
          </CardRadio>
        )}
      </div>
      {!disabled && (
        <div className="flex flex-col gap-1">
          <SearchInput
            value={query}
            onValueChange={setQuery}
            placeholder={field.placeholder || 'Search for your trade'}
            aria-label={field.placeholder || 'Search for your trade'}
            className="max-w-none"
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || !text) return
              event.preventDefault()
              pick(matches[0]?.value ?? text)
            }}
          />
          {text && (
            <div className="flex flex-col rounded-md border p-1">
              {matches.map((option) => (
                <Button
                  key={option.value}
                  type="button"
                  variant="ghost"
                  className="h-auto flex-col items-start gap-0 py-2 text-left font-normal whitespace-normal"
                  onClick={() => pick(option.value)}
                >
                  <span>{option.label}</span>
                  {option.description && (
                    <span className="text-muted-foreground text-xs">{option.description}</span>
                  )}
                </Button>
              ))}
              {!exactMatch && (
                <Button
                  type="button"
                  variant="ghost"
                  className="h-auto justify-start py-2 text-left font-normal whitespace-normal"
                  onClick={() => pick(text)}
                >
                  Use “{text}”
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
